/**
 * KYS form outputs (ADR 0033): report rows → the paper-form PDF documents (FormExport) and the XLSX sheets.
 * Labels come from i18n. No person names anywhere: the operator box says whether the operator was
 * identified from the card session, the approver box carries the ROLE and the time (owner decision).
 */
import {
  KYS_FR11_FORM,
  KYS_FR12_FORM,
  fr11Derived,
  kysDocumentNo,
  kysFormLabel,
  kysFr12Lines,
  type KysApproverRole,
  type KysFr11Row,
  type KysFormRef,
  type KysFr12Form,
} from "@fts/core";
import type { TFn } from "../i18n";
import { formatDateTimeIn, formatDay, type ExportLocale, type FormExport, type FormRefText, type Sheet } from "./export/sheet";

export const approverRoleLabel = (t: TFn, role: KysApproverRole): string => (role === "platform_admin" ? t("tenant.platformAdmin") : t(`role.${role}`));

/**
 * `footer`: the tenant's own controlled-document line (tenants.form_footer); null/empty = the neutral default,
 * which names no company (customer tenants must not see Expartise's KYS wording).
 */
const formLabels = (t: TFn, footer: string | null): FormExport["labels"] => ({
  code: t("kys.form.code"),
  revision: t("kys.form.revision"),
  revisionDate: t("kys.form.revisionDate"),
  page: t("reports.export.page"),
  documentNo: t("kys.fr12.field.documentNo"),
  controlled: footer?.trim() || t("kys.form.controlledDefault"),
});

const title = (lang: ExportLocale, f: { titleTr: string; titleEn: string }) => (lang === "en" && f.titleEn.trim() ? f.titleEn : f.titleTr);

const refOf = (lang: ExportLocale, f: KysFormRef): FormRefText => ({ code: f.code, revision: f.revision, revisionDate: f.revisionDate, title: title(lang, f) });

/** File-level summary of the documents' identities: one value, or the distinct values joined ("00, 01"). */
function summary(refs: FormRefText[], fallback: FormRefText): Pick<FormExport, "code" | "revision" | "revisionDate" | "title"> {
  if (refs.length === 0) return fallback;
  const uniq = (xs: (string | null)[]) => [...new Set(xs.map((x) => x ?? "—"))].join(", ");
  const same = refs.every((r) => r.code === refs[0]!.code && r.revision === refs[0]!.revision);
  return {
    code: uniq(refs.map((r) => r.code)),
    revision: uniq(refs.map((r) => r.revision)),
    revisionDate: same ? refs[0]!.revisionDate : null,
    title: refs[0]!.title,
  };
}
const model = (r: { make: string | null; model: string | null }) => [r.make, r.model].filter(Boolean).join(" ") || "—";

/* ---------------- FR-12 ---------------- */

/** The FR-12 signature boxes: operator (identified or not) and approver (role + time or pending). */
export function fr12Signatures(t: TFn, f: KysFr12Form, tz: string, lang: ExportLocale): [string, string][] {
  const when = (iso: string) => formatDateTimeIn(iso, tz, lang);
  return [
    [t("kys.fr12.sign.operator"), `${f.operatorKnown ? t("kys.fr12.operatorKnown") : t("kys.fr12.operatorUnknown")} · ${when(f.submittedAt)}`],
    [t("kys.fr12.sign.approver"), f.approvedAt && f.approvedRole ? `${approverRoleLabel(t, f.approvedRole)} · ${when(f.approvedAt)}` : t("kys.fr12.approvalPending")],
  ];
}

/** One PDF document per filled-in form, in the paper's layout. */
export function fr12FormExport(t: TFn, lang: ExportLocale, forms: KysFr12Form[], tz: string, footer: string | null): FormExport {
  const mark = t("kys.fr12.mark");
  const fallback: FormRefText = { code: KYS_FR12_FORM.code, revision: KYS_FR12_FORM.revision, revisionDate: KYS_FR12_FORM.revisionDate, title: title(lang, KYS_FR12_FORM) };
  return {
    ...summary(forms.map((f) => refOf(lang, f.form)), fallback),
    orientation: "portrait",
    labels: formLabels(t, footer),
    documents: forms.map((f) => ({
      // Each form's own identity: a period may mix revisions or re-coded copies.
      ref: refOf(lang, f.form),
      fields: [
        [t("kys.fr12.field.date"), formatDateTimeIn(f.submittedAt, tz, lang)],
        [t("kys.fr12.field.documentNo"), kysDocumentNo(f.form.code, f.documentNo) ?? "—"],
        [t("kys.fr12.field.vehicle"), `${f.fleetNo} / ${model(f)}`],
        [t("kys.fr12.field.site"), f.siteName ?? "—"],
        [t("kys.fr12.field.shift"), f.shiftName ?? "—"],
        [t("kys.fr12.field.result"), t(`checklist.result.${f.result}`)],
      ],
      table: {
        heading: t("kys.fr12.tableHeading"),
        columns: [
          { header: t("kys.fr12.col.item"), width: "*" },
          { header: t("kys.fr12.col.ok"), width: 46, align: "center" },
          { header: t("kys.fr12.col.notOk"), width: 52, align: "center" },
          { header: t("kys.fr12.col.note"), width: 170 },
        ],
        rows: kysFr12Lines(f, lang, t("kys.fr12.na")).map((l) => [l.text, l.ok ? mark : "", l.notOk ? mark : "", l.note]),
      },
      signatures: fr12Signatures(t, f, tz, lang),
      note: t("kys.fr12.noLock"),
    })),
  };
}

/** XLSX: one row per form item (long format), with the form's document number and approval. */
export function fr12Sheet(t: TFn, lang: ExportLocale, forms: KysFr12Form[]): Sheet {
  return {
    name: "FR-12",
    columns: [
      { header: t("kys.fr12.field.documentNo"), type: "text", width: 18 },
      { header: t("kys.form.label"), type: "text", width: 16 },
      { header: t("kys.fr12.field.date"), type: "datetime", width: 16 },
      { header: t("reports.col.vehicle"), type: "text", width: 10 },
      { header: t("kys.fr11.col.model"), type: "text", width: 18 },
      { header: t("kys.fr12.field.site"), type: "text", width: 20 },
      { header: t("kys.fr12.col.code"), type: "text", width: 6 },
      { header: t("kys.fr12.col.item"), type: "text", width: 36 },
      { header: t("kys.fr12.col.answer"), type: "text", width: 12 },
      { header: t("kys.fr12.col.note"), type: "text", width: 30 },
      { header: t("kys.fr12.field.result"), type: "text", width: 16 },
      { header: t("kys.fr12.col.operatorKnown"), type: "bool", width: 12 },
      { header: t("kys.fr12.sign.approver"), type: "text", width: 16 },
      { header: t("kys.fr12.col.approvedAt"), type: "datetime", width: 16 },
    ],
    rows: forms.flatMap((f) =>
      [...f.answers]
        .sort((a, b) => a.position - b.position)
        .map((a) => [
          kysDocumentNo(f.form.code, f.documentNo),
          kysFormLabel(f.form),
          f.submittedAt,
          f.fleetNo,
          model(f),
          f.siteName,
          a.code,
          lang === "en" && a.textEn ? a.textEn : a.textTr,
          t(`checklist.answer.${a.answer}`),
          a.note,
          t(`checklist.result.${f.result}`),
          f.operatorKnown,
          f.approvedRole ? approverRoleLabel(t, f.approvedRole) : null,
          f.approvedAt,
        ]),
    ),
  };
}

/* ---------------- FR-11 ---------------- */

export type Fr11Line = {
  equipmentNo: string;
  name: string;
  model: string;
  serialNo: string;
  location: string;
  responsible: string;
  interval: string;
  lastPeriodic: string;
  nextPeriodic: string;
  lastInspection: string;
  nextInspection: string;
  status: string;
};

/** One FR-11 row as the paper shows it (every cell text, in the report language). */
export function fr11Line(t: TFn, lang: ExportLocale, r: KysFr11Row, today: string): Fr11Line {
  const d = fr11Derived(r, today);
  const day = (v: string | null) => (v ? formatDay(v, lang) : "—");
  return {
    equipmentNo: r.fleetNo,
    name: t(`vehicleType.${r.vehicleType}`),
    model: model(r),
    serialNo: r.serialNo ?? "—",
    location: r.siteName ?? "—",
    responsible: r.responsibleRole ? t(`kys.mrole.${r.responsibleRole}`) : "—",
    interval: r.intervalMonths ? t("kys.fr11.months", { n: r.intervalMonths }) : "—",
    lastPeriodic: day(r.lastPeriodicOn),
    nextPeriodic: day(d.nextPeriodicOn),
    lastInspection: day(r.lastInspectionOn),
    nextInspection: day(d.nextInspectionOn),
    status: t(`kys.fr11.status.${d.status}`),
  };
}

const FR11_KEYS: (keyof Fr11Line)[] = ["equipmentNo", "name", "model", "serialNo", "location", "responsible", "interval", "lastPeriodic", "nextPeriodic", "lastInspection", "nextInspection", "status"];

export function fr11Columns(t: TFn): string[] {
  return [
    t("kys.fr11.col.no"),
    t("kys.fr11.col.name"),
    t("kys.fr11.col.model"),
    t("kys.fr11.col.serial"),
    t("kys.fr11.col.location"),
    t("kys.fr11.col.responsible"),
    t("kys.fr11.col.interval"),
    t("kys.fr11.col.lastPeriodic"),
    t("kys.fr11.col.nextPeriodic"),
    t("kys.fr11.col.lastInspection"),
    t("kys.fr11.col.nextInspection"),
    t("kys.fr11.col.status"),
  ];
}

/** The FR-11 paper form: purpose and scope, the equipment table, the preparer box (role + time). */
export function fr11FormExport(
  t: TFn,
  lang: ExportLocale,
  rows: KysFr11Row[],
  opts: { today: string; tz: string; footer: string | null; scope: string; generatedAt: string; role: string },
): FormExport {
  const headers = fr11Columns(t);
  const ref = refOf(lang, KYS_FR11_FORM);
  return {
    ...ref,
    orientation: "landscape",
    labels: formLabels(t, opts.footer),
    documents: [
      {
        ref,
        fields: [
          [t("kys.fr11.field.date"), formatDay(opts.today, lang)],
          [t("kys.fr12.field.site"), opts.scope],
        ],
        paragraphs: [
          { heading: t("kys.fr11.purposeTitle"), text: t("kys.fr11.purpose") },
          { heading: t("kys.fr11.scopeTitle"), text: t("kys.fr11.scope") },
        ],
        table: {
          columns: headers.map((header, i) => ({ header, width: i === 1 || i === 2 || i === 4 ? ("*" as const) : undefined })),
          rows: rows.map((r) => {
            const l = fr11Line(t, lang, r, opts.today);
            return FR11_KEYS.map((k) => l[k]);
          }),
        },
        signatures: [[t("kys.fr11.preparedBy"), `${opts.role} · ${formatDateTimeIn(opts.generatedAt, opts.tz, lang)}`]],
        note: t("kys.fr11.formNote"),
      },
    ],
  };
}

/** XLSX: the same columns, dates as real dates. */
export function fr11Sheet(t: TFn, lang: ExportLocale, rows: KysFr11Row[], today: string): Sheet {
  const h = fr11Columns(t);
  return {
    name: "FR-11",
    columns: [
      { header: h[0]!, type: "text", width: 10 },
      { header: h[1]!, type: "text", width: 16 },
      { header: h[2]!, type: "text", width: 18 },
      { header: h[3]!, type: "text", width: 14 },
      { header: h[4]!, type: "text", width: 22 },
      { header: h[5]!, type: "text", width: 18 },
      { header: t("kys.fr11.col.intervalMonths"), type: "int", width: 10 },
      { header: h[7]!, type: "date", width: 12 },
      { header: h[8]!, type: "date", width: 12 },
      { header: h[9]!, type: "date", width: 12 },
      { header: h[10]!, type: "date", width: 12 },
      { header: h[11]!, type: "text", width: 16 },
    ],
    rows: rows.map((r) => {
      const d = fr11Derived(r, today);
      const l = fr11Line(t, lang, r, today);
      return [l.equipmentNo, l.name, l.model, r.serialNo, r.siteName, r.responsibleRole ? l.responsible : null, r.intervalMonths, r.lastPeriodicOn, d.nextPeriodicOn, r.lastInspectionOn, d.nextInspectionOn, l.status];
    }),
  };
}
